/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '../../../theme/theme';
import {BackButton} from '../../buttons/BackButton';
import {Breadcrumb, BreadcrumbItemData} from './Breadcrumb';
import {DrawerHeaderActions} from './DrawerHeaderActions';

export interface DrawerHeaderProps {
  breadcrumbItems: BreadcrumbItemData[];
  onBack: () => void;
  onClose: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const useStyles = makeStyles(() => ({
  header : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'row',
    justifyContent  : 'space-between',
    alignItems      : 'center',
    width           : '100%',
    height          : '56px',
    backgroundColor : dlsColor('grey6'),
    borderRadius    : '0 12px 0 0'
  },
  // shrinks to whatever the (fixed-width) actions group leaves, so a long
  // breadcrumb wraps/truncates instead of pushing the actions off-screen
  // on narrow (mobile, 390px) sheets ...
  left : {
    flex          : '1 1 auto',
    minWidth      : 0,
    display       : 'flex',
    flexDirection : 'row',
    alignItems    : 'center',
    padding       : '0 16px',
    gap           : '16px'
  },
  backControl : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '4px'
  },
  backLabel : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '12px',
    lineHeight : '16px',
    color      : dlsColor('grey1')
  }
}));

/**
 * DrawerHeader renders the Asset Details Drawer's top bar: a `BackButton` +
 * "Back" label on the left, the `Breadcrumb` trail beside it, and the
 * `DrawerHeaderActions` cluster (expand-list toggle + `CloseButton`) on the
 * right. `BackButton` renders via `IconButton`'s control-button mode, which
 * has no `label` support of its own (its early-return branch never reads
 * `label`/`labelPlacement`), so the "Back" text here is a small sibling
 * span rather than a `BackButton` prop.
 *
 * ```typescript
 *
 *   import {DrawerHeader} from
 *     '@ion/dls/src/components/drawer/current/DrawerHeader';
 *   ...
 *
 *   <DrawerHeader breadcrumbItems={[
 *     {label: 'Assets', onClick: onAssetsClick},
 *     {label: 'Vehicle 13', isActive: true}
 *   ]}
 *     onBack={onBack}
 *     onClose={onClose}
 *     isExpanded={isExpanded}
 *     onToggleExpand={onToggleExpand} />
 *
 * ```
 *
 */
export const DrawerHeader = memo(({
  breadcrumbItems,
  onBack,
  onClose,
  isExpanded,
  onToggleExpand
}: DrawerHeaderProps) => {
  const classes = useStyles();
  return (
    <div className={classes.header}>
      <div className={classes.left}>
        <span className={classes.backControl}>
          <BackButton onClick={onBack} />
          <span className={classes.backLabel}>
            {translate('common:editors.shared.back')}
          </span>
        </span>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <DrawerHeaderActions isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onClose={onClose} />
    </div>
  );
});

export default DrawerHeader;
